import React, { useState, useRef } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  TextField,
  Stack,
  Link
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import authService from '../../services/authService';
import FormErrorWrapper from '../common/FormErrorWrapper';

function ResetPassword() {
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState({});
  const [codeSent, setCodeSent] = useState(false);
  const [success, setSuccess] = useState(false);
  const inputRefs = useRef([]);
  const navigate = useNavigate();

  // Handle email submission to request password reset
  const handleSendCode = async (e) => {
    e.preventDefault();
    setError({});
    
    // Validate email
    if (!email || !email.trim()) {
      setError({ email: 'Email is required' });
      return;
    }
    
    setIsSending(true);

    try {
      await authService.requestPasswordReset(email);
      setCodeSent(true);
      // Focus on the first input once code is sent
      setTimeout(() => {
        if (inputRefs.current[0]) {
          inputRefs.current[0].focus();
        }
      }, 100);
    } catch (err) {
      // We don't show specific errors about whether the email exists in the system
      // for security reasons, but we still track errors for debugging
      console.error('Error sending reset code:', err);
    } finally {
      setIsSending(false);
    }
  };

  // Handle reset password form submission
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError({});
    
    // Client-side validation
    if (verificationCode.some(digit => digit === '')) {
      setError({ code: 'Please enter the complete verification code' });
      return;
    }
    
    if (!password) {
      setError({ password: 'Password is required' });
      return;
    }
    
    if (password !== confirmPassword) {
      setError({ confirm_password: 'Passwords do not match' });
      return;
    }
    
    setIsLoading(true);

    try {
      const code = verificationCode.join('');
      const response = await authService.resetPassword({
        email,
        code,
        password,
        confirm_password: confirmPassword
      });

      if (response.success) {
        setSuccess(true);
        // Redirect to login page after successful reset
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }
    } catch (err) {
      if (err.response?.data) {
        setError(err.response.data);
      } else {
        setError({ non_field_errors: 'Failed to reset password. Please try again.' });
      }
      
      // If there's a code error, clear the code inputs
      if (err.response?.data?.code) {
        setVerificationCode(['', '', '', '', '', '']);
        if (inputRefs.current[0]) {
          inputRefs.current[0].focus();
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeChange = (index, value) => {
    // Allow only digits
    if (value !== '' && !/^\d+$/.test(value)) return;

    // Update the verification code state
    const newCode = [...verificationCode];

    // Handle paste event with multiple characters
    if (value.length > 1) {
      const digits = value.split('').slice(0, 6 - index);

      digits.forEach((digit, i) => {
        if (index + i < 6) {
          newCode[index + i] = digit;
        }
      });

      setVerificationCode(newCode);

      // Focus on appropriate input after paste
      const nextIndex = Math.min(index + digits.length, 5);
      if (inputRefs.current[nextIndex]) {
        inputRefs.current[nextIndex].focus();
      }
      return;
    }

    newCode[index] = value;
    setVerificationCode(newCode);

    // Auto-focus next input when a digit is entered
    if (value !== '' && index < 5) {
      if (inputRefs.current[index + 1]) {
        inputRefs.current[index + 1].focus();
      }
    }
  };

  const handleKeyDown = (index, e) => {
    // Move to previous input on backspace if current input is empty
    if (e.key === 'Backspace' && index > 0 && verificationCode[index] === '') {
      if (inputRefs.current[index - 1]) {
        inputRefs.current[index - 1].focus();
      }
    }

    // Navigate between inputs with arrow keys
    if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      if (inputRefs.current[index - 1]) {
        inputRefs.current[index - 1].focus();
      }
    }

    if (e.key === 'ArrowRight' && index < 5) {
      e.preventDefault();
      if (inputRefs.current[index + 1]) {
        inputRefs.current[index + 1].focus();
      }
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Paper elevation={3} sx={{ p: 4, mt: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <LockResetIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />

        <Typography component="h1" variant="h5" gutterBottom>
          Reset Password
        </Typography>

        {error.non_field_errors && (
          <Typography color="error" sx={{ mt: 2, mb: 2 }}>
            {error.non_field_errors}
          </Typography>
        )}

        {success ? (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Password Successfully Reset!
            </Typography>
            <Typography variant="body2">
              Redirecting you to the login page...
            </Typography>
          </Box>
        ) : (
          <>
            {!codeSent ? (
              <Box component="form" onSubmit={handleSendCode} sx={{ width: '100%', mt: 1 }}>
                <Typography variant="body1" align="center" sx={{ mb: 3 }}>
                  Enter your email address to receive a password reset code.
                </Typography>
                
                <FormErrorWrapper error={error.email}>
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="email"
                    label="Email Address"
                    name="email"
                    autoComplete="email"
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isSending}
                  />
                </FormErrorWrapper>
                
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  disabled={isSending}
                  sx={{ mt: 3, mb: 2 }}
                >
                  {isSending ? <CircularProgress size={24} /> : 'Send Reset Code'}
                </Button>
                
                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <Link component={RouterLink} to="/login" variant="body2">
                    Back to Login
                  </Link>
                </Box>
              </Box>
            ) : (
              <Box component="form" onSubmit={handleResetPassword} sx={{ width: '100%', mt: 1 }}>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  We've sent a 6-digit verification code to your email address 
                  {email && <Box component="span" fontWeight="medium"> ({email})</Box>}.
                  If the email exists in our system, you'll receive a code shortly.
                </Typography>

                <FormErrorWrapper error={error.code}>
                  <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="center"
                    sx={{ mb: 3 }}
                  >
                    {[0, 1, 2, 3, 4, 5].map((index) => (
                      <TextField
                        key={index}
                        inputRef={(el) => (inputRefs.current[index] = el)}
                        variant="outlined"
                        margin="dense"
                        inputProps={{
                          maxLength: 1,
                          style: {
                            textAlign: 'center',
                            fontSize: '1.5rem',
                            padding: '8px',
                            width: '100%'
                          }
                        }}
                        sx={{ width: '48px' }}
                        value={verificationCode[index]}
                        onChange={(e) => handleCodeChange(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        onPaste={(e) => {
                          e.preventDefault();
                          const pasteData = e.clipboardData.getData('text/plain');
                          handleCodeChange(index, pasteData);
                        }}
                        disabled={isLoading}
                      />
                    ))}
                  </Stack>
                </FormErrorWrapper>

                <FormErrorWrapper error={error.password}>
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    name="password"
                    label="New Password"
                    type="password"
                    id="password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                  />
                </FormErrorWrapper>

                <FormErrorWrapper error={error.confirm_password}>
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    name="confirmPassword"
                    label="Confirm New Password"
                    type="password"
                    id="confirmPassword"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={isLoading}
                  />
                </FormErrorWrapper>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  disabled={isLoading}
                  sx={{ mt: 3, mb: 2 }}
                >
                  {isLoading ? <CircularProgress size={24} /> : 'Reset Password'}
                </Button>

                <Button
                  fullWidth
                  variant="text"
                  onClick={() => {
                    setCodeSent(false);
                    setVerificationCode(['', '', '', '', '', '']);
                  }}
                  disabled={isLoading || isSending}
                  sx={{ mt: 1 }}
                >
                  Try Different Email
                </Button>
              </Box>
            )}
          </>
        )}
      </Paper>
    </Container>
  );
}

export default ResetPassword;